import { HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { getStoreGroupReportClients } from 'services/storeGroupReport';
import type {
  ReportPeriod,
  StoreGroupReportClients,
} from 'services/interfaces/storeGroupReport';
import PaginationFooter from 'shared/components/Logs/PaginationFooter';
import ReportClientsTable from './ReportClientsTable';
import ReportPeriodFilter from './ReportPeriodFilter';

const PER_PAGE = 20;

interface GroupClientsTabProps {
  storeGroupId: number;
}

// Só leitura — sem o disparo de WhatsApp que a aba Clientes da franquia tem no ionic
// (decisão do Eden: fica pra outra fatia).
const GroupClientsTab: React.FC<GroupClientsTabProps> = ({ storeGroupId }) => {
  const [period, setPeriod] = useState<ReportPeriod | null>(null);
  const [page, setPage] = useState(1);
  const [clients, setClients] = useState<StoreGroupReportClients | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (p: ReportPeriod, pageArg: number) => {
      setLoading(true);
      setError(false);
      try {
        const res = await getStoreGroupReportClients(storeGroupId, p, {
          page: pageArg,
          perPage: PER_PAGE,
          sort: 'balance',
          dir: 'desc',
        });
        setClients(res);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [storeGroupId],
  );

  const handlePeriodApply = (p: ReportPeriod) => {
    setPeriod(p);
    setPage(1);
    load(p, 1);
  };

  useEffect(() => {
    if (!period) return;
    load(period, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = clients?.pagination.totalPages ?? 1;

  return (
    <Stack spacing={4}>
      <ReportPeriodFilter onApply={handlePeriodApply} />

      {loading && (
        <HStack justify="center" py="2rem">
          <Spinner color="yooga.500" />
          <Text>Carregando…</Text>
        </HStack>
      )}

      {error && (
        <Text color="red.500">Não foi possível carregar os clientes.</Text>
      )}

      {clients && !loading && (
        <Stack spacing={4}>
          <ReportClientsTable data={clients.data} />
          {totalPages > 1 && (
            <PaginationFooter
              visibleLength={clients.pagination.totalItems}
              page={page}
              setPage={setPage}
              pageCount={totalPages}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default GroupClientsTab;
